import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { parseISO, startOfDay } from "date-fns";
import { useData } from "@/contexts/DataContext";
import { useBaby } from "@/contexts/BabyContext";
import { useBreastfeedTimer } from "@/contexts/BreastfeedTimerContext";
import { getTodaySummary } from "@/lib/calculations";
import { getMedications, getMedicineLog } from "@/lib/health-firestore";
import { getMealLogsToday } from "@/lib/meal-firestore";
import { getWaterTodayMl } from "@/lib/water-firestore";
import { getMonthGuideForSolidsAge } from "@/lib/solidsGuideContent";
import type { Medication } from "@/types";
import Header from "@/components/Header";
import StatCard from "@/components/StatCard";
import BottomNavigation from "@/components/BottomNavigation";
import AddBabyModal from "@/components/AddBabyModal";
import LogSheet, { type LogType } from "@/components/LogSheet";
import DayTimeline from "@/components/DayTimeline";
import SolidsCard from "@/components/SolidsCard";
import MedicineDoseSheet from "@/components/MedicineDoseSheet";
import AddMedicineModal from "@/components/health/AddMedicineModal";
import MealLogSheet from "@/components/MealLogSheet";
import WaterLogSheet from "@/components/WaterLogSheet";
import MealPlanCard from "@/components/MealPlanCard";
import VaxDueCard from "@/components/VaxDueCard";
import ActivityChartWidget from "@/components/ActivityChartWidget";
import GrowthChartWidget from "@/components/GrowthChartWidget";

const BabyBottleIcon = () => <span className="text-xl">🍼</span>;
const SleepIcon = () => <span className="text-xl">😴</span>;
const DiaperIcon = () => <span className="text-xl">🧷</span>;
const GrowthIcon = () => <span className="text-xl">📊</span>;
const MedicineIcon = () => <span className="text-xl">💊</span>;
const MealIcon = () => <span className="text-xl">🥄</span>;
const WaterIcon = () => <span className="text-xl">💧</span>;
const MilkIcon = () => <span className="text-xl">🍼</span>;

// Safety net so old prescriptions (saved before we persisted endDate from OCR
// duration) don't haunt the medicine tile forever. If there's no explicit
// endDate, treat anything older than 60 days as expired — most baby
// prescriptions are 5–14 days, so 60 days is generous and avoids stale chips.
const MEDICATION_OPEN_ENDED_FALLBACK_DAYS = 60;

function isMedicationActiveToday(med: Medication): boolean {
  const today = startOfDay(new Date());
  const start = startOfDay(parseISO(med.startDate));
  if (start > today) return false;
  if (med.endDate) {
    const end = startOfDay(parseISO(med.endDate));
    return today <= end;
  }
  // No explicit end — auto-expire after the fallback window
  const ageInDays = (today.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
  return ageInDays <= MEDICATION_OPEN_ENDED_FALLBACK_DAYS;
}

const Index = () => {
  const navigate = useNavigate();
  const { data } = useData();
  const { babies, loading: babyLoading, activeBaby } = useBaby();
  const { timerState, liveLeft, liveRight, liveTotal } = useBreastfeedTimer();

  const showFeedBanner = timerState.isActive && timerState.babyId === activeBaby?.id;
  const solidsActive = !!activeBaby?.solidsStartDate;

  const formatSec = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  };

  const [showAddBabyModal, setShowAddBabyModal] = useState(false);
  const [logSheet, setLogSheet] = useState<LogType | null>(null);
  const [showMedSheet, setShowMedSheet] = useState(false);
  const [showMealSheet, setShowMealSheet] = useState(false);
  const [mealSheetSuggestion, setMealSheetSuggestion] = useState<string | undefined>(undefined);
  const [showWaterSheet, setShowWaterSheet] = useState(false);
  const [medications, setMedications] = useState<Medication[]>([]);
  const [medLog, setMedLog] = useState<Record<string, { done: boolean; timestamp: string }>>({});
  const [showAddMedicineModal, setShowAddMedicineModal] = useState(false);
  const [mealCount, setMealCount] = useState(0);
  const [waterMl, setWaterMl] = useState(0);

  // Reload after deployment
  useEffect(() => {
    if (window.__needsAppReload) {
      window.__needsAppReload = false;
      window.location.reload();
    }
  }, []);

  useEffect(() => {
    if (!babyLoading && babies.length === 0) {
      setShowAddBabyModal(true);
    } else {
      setShowAddBabyModal(false);
    }
  }, [babyLoading, babies]);

  const loadMedData = useCallback(async () => {
    if (!activeBaby?.id) return;
    try {
      const [meds, log] = await Promise.all([
        getMedications(activeBaby.id),
        getMedicineLog(activeBaby.id),
      ]);
      setMedications(meds);
      setMedLog(log);
    } catch {}
  }, [activeBaby?.id]);

  const loadSolidsData = useCallback(async () => {
    if (!activeBaby?.id || !activeBaby.solidsStartDate) return;
    try {
      const [logs, ml] = await Promise.all([
        getMealLogsToday(activeBaby.id),
        getWaterTodayMl(activeBaby.id),
      ]);
      setMealCount(logs.length);
      setWaterMl(ml);
    } catch {}
  }, [activeBaby?.id, activeBaby?.solidsStartDate]);

  useEffect(() => { loadMedData(); },    [loadMedData]);
  useEffect(() => { loadSolidsData(); }, [loadSolidsData]);

  const handleAddFeed = () => navigate("/add-feed");
  const handleAddSleep = () => navigate("/add-sleep");
  const handleAddDiaper = () => navigate("/add-diaper");
  const handleAddGrowth = () => navigate("/add-growth");

  const summary = getTodaySummary(data.feeds, data.sleeps, data.diapers);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // --- Feeds / Milk tile ---
  const todayFeeds = data.feeds.filter((f) => new Date(f.timestamp) >= today);
  const milkFeeds = todayFeeds.filter((f) => f.type === 'bottle' || f.type === 'breastfeed');
  const totalMl = milkFeeds.filter((f) => f.type === 'bottle').reduce((sum, f) => sum + (f.amount ?? 0), 0);
  const totalMins = milkFeeds.filter((f) => f.type === 'breastfeed').reduce((sum, f) => sum + (f.duration ?? 0), 0);

  // When solids active: show count of milk feeds; otherwise show ml/mins
  let feedValue: React.ReactNode;
  if (solidsActive) {
    feedValue = milkFeeds.length > 0
      ? `${milkFeeds.length} feed${milkFeeds.length !== 1 ? 's' : ''}`
      : '–';
  } else if (totalMl > 0 && totalMins > 0) {
    feedValue = `${totalMl}ml · ${totalMins}m`;
  } else if (totalMl > 0) {
    feedValue = `${totalMl}ml`;
  } else if (totalMins > 0) {
    feedValue = `${totalMins}m`;
  } else {
    feedValue = '–';
  }

  // --- Growth tile ---
  const latestWeight = data.growth.filter((g) => g.metric === 'weight').sort((a, b) => b.date.getTime() - a.date.getTime())[0] ?? null;
  const latestHeight = data.growth.filter((g) => g.metric === 'height').sort((a, b) => b.date.getTime() - a.date.getTime())[0] ?? null;
  const latestHC    = data.growth.filter((g) => g.metric === 'headCircumference').sort((a, b) => b.date.getTime() - a.date.getTime())[0] ?? null;
  const growthValue: React.ReactNode =
    latestWeight ? `${latestWeight.value}kg`
    : latestHeight ? `${latestHeight.value}cm`
    : latestHC    ? `${latestHC.value}cm`
    : '–';

  // --- Medicine tile ---
  const activeMeds = medications.filter(m => m.isActive && isMedicationActiveToday(m));
  const totalDoses = activeMeds.reduce((sum, m) => sum + m.timings.length, 0);
  const doneDoses  = activeMeds.reduce((sum, m) => sum + m.timings.filter(t => medLog[`${m.id}_${t}`]?.done).length, 0);
  const medValue = activeMeds.length === 0 ? 'No meds' : `${doneDoses}/${totalDoses}`;

  // --- Meals tile (X/Y) ---
  const monthGuide = solidsActive
    ? getMonthGuideForSolidsAge(activeBaby!.solidsStartDate!)
    : null;
  const expectedMeals = monthGuide?.expectedMealsPerDay ?? 2;
  const mealValue = `${mealCount}/${expectedMeals}`;

  // --- Water tile ---
  const waterValue = waterMl > 0 ? `${waterMl}ml` : '–';

  const openMealSheet = (suggestion?: string) => {
    setMealSheetSuggestion(suggestion);
    setShowMealSheet(true);
  };

  return (
    <div className="min-h-screen bg-background pb-24 lg:pb-8">
      <Header />

      {showFeedBanner && (
        <div
          className="mx-5 mt-3 mb-1 rounded-2xl p-3.5 flex items-center gap-3 cursor-pointer active:opacity-90"
          style={{ background: 'linear-gradient(to right, hsl(var(--primary)), hsl(var(--sleep-accent)))' }}
          onClick={() => navigate('/add-feed')}
        >
          <span className="text-2xl">🤱</span>
          <div className="flex-1 min-w-0">
            <p className="text-white text-xs font-semibold">Breastfeeding in progress</p>
            <p className="text-white/80 text-xs tabular-nums">
              L {formatSec(liveLeft)} · R {formatSec(liveRight)} · Total {formatSec(liveTotal)}
            </p>
          </div>
          <span className="text-white text-xs font-semibold bg-white/25 px-3 py-1.5 rounded-full flex-shrink-0">
            Manage →
          </span>
        </div>
      )}

      {/* ── Stat tiles ─────────────────────────────────────────────────────────── */}
      <div className="stat-tile-grid mt-4 pb-1">
        {solidsActive ? (
          // ── Solids-active order: Meals, Water, Milk, Growth, Diaper, Sleep ──
          <>
            <div>
              <StatCard
                type="meal"
                icon={<MealIcon />}
                title="Meals"
                value={mealValue}
                onClick={() => openMealSheet()}
                onAddClick={() => openMealSheet()}
              />
            </div>
            <div>
              <StatCard
                type="water"
                icon={<WaterIcon />}
                title="Water"
                value={waterValue}
                onClick={() => setShowWaterSheet(true)}
                onAddClick={() => setShowWaterSheet(true)}
              />
            </div>
            <div>
              <StatCard
                type="milk"
                icon={<MilkIcon />}
                title="Milk"
                value={feedValue}
                onClick={() => setLogSheet('feed')}
                onAddClick={handleAddFeed}
              />
            </div>
            <div>
              <StatCard
                type="growth"
                icon={<GrowthIcon />}
                title="Growth"
                value={growthValue}
                onAddClick={handleAddGrowth}
              />
            </div>
            <div>
              <StatCard
                type="diaper"
                icon={<DiaperIcon />}
                title="Diapers"
                value={summary.diaperCount.toString()}
                onClick={() => setLogSheet('diaper')}
                onAddClick={handleAddDiaper}
              />
            </div>
            <div>
              <StatCard
                type="sleep"
                icon={<SleepIcon />}
                title="Sleep"
                value={`${summary.totalSleepHours}h`}
                onClick={() => setLogSheet('sleep')}
                onAddClick={handleAddSleep}
              />
            </div>
            <div>
              <StatCard
                type="medicine"
                icon={<MedicineIcon />}
                title="Meds"
                value={medValue}
                onClick={activeMeds.length > 0 ? () => setShowMedSheet(true) : () => setShowAddMedicineModal(true)}
                onAddClick={() => setShowAddMedicineModal(true)}
              />
            </div>
          </>
        ) : (
          // ── Pre-solids order: Feeds, Diapers, Sleep, Growth, Medicine ──
          <>
            <div>
              <StatCard
                type="feed"
                icon={<BabyBottleIcon />}
                title="Feeds"
                value={feedValue}
                onClick={() => setLogSheet('feed')}
                onAddClick={handleAddFeed}
              />
            </div>
            <div>
              <StatCard
                type="diaper"
                icon={<DiaperIcon />}
                title="Diapers"
                value={summary.diaperCount.toString()}
                onClick={() => setLogSheet('diaper')}
                onAddClick={handleAddDiaper}
              />
            </div>
            <div>
              <StatCard
                type="sleep"
                icon={<SleepIcon />}
                title="Sleep"
                value={`${summary.totalSleepHours}h`}
                onClick={() => setLogSheet('sleep')}
                onAddClick={handleAddSleep}
              />
            </div>
            <div>
              <StatCard
                type="growth"
                icon={<GrowthIcon />}
                title="Growth"
                value={growthValue}
                onAddClick={handleAddGrowth}
              />
            </div>
            <div>
              <StatCard
                type="medicine"
                icon={<MedicineIcon />}
                title="Meds"
                value={medValue}
                onClick={activeMeds.length > 0 ? () => setShowMedSheet(true) : () => setShowAddMedicineModal(true)}
                onAddClick={() => setShowAddMedicineModal(true)}
              />
            </div>
          </>
        )}
      </div>

      {/* ── Alerts + solids section ────────────────────────────────────────────── */}
      <div className="px-5 mt-3 space-y-3">
        {/* Vaccine alerts — booklet-driven, dismissible per vaccine */}
        <VaxDueCard />
        {/* Today's meal plan card — only when solids started */}
        {solidsActive && (
          <MealPlanCard
            mealLoggedCount={mealCount}
            onLogMeal={(suggestion) => openMealSheet(suggestion)}
          />
        )}
        {/* Prep card or week planner entry */}
        <SolidsCard />
      </div>

      {/* Desktop two-column layout */}
      <div className="lg:flex lg:gap-4 lg:px-5 lg:mt-4 lg:items-start">
        <div className="lg:flex-1 lg:min-w-0">
          <DayTimeline />
        </div>
        <div className="lg:w-[340px] lg:flex-shrink-0">
          <div className="px-5 mt-8 mb-4 lg:px-0 lg:mt-6">
            <h2 className="text-lg font-semibold text-foreground mb-4">Trends</h2>
            <div className="space-y-4">
              <ActivityChartWidget />
              <GrowthChartWidget />
            </div>
          </div>
        </div>
      </div>

      <BottomNavigation />

      <AddBabyModal
        isOpen={showAddBabyModal}
        onClose={() => setShowAddBabyModal(false)}
      />

      {logSheet && (
        <LogSheet
          type={logSheet}
          isOpen={true}
          onClose={() => setLogSheet(null)}
        />
      )}

      <MedicineDoseSheet
        isOpen={showMedSheet}
        onClose={() => { setShowMedSheet(false); loadMedData(); }}
      />

      {showAddMedicineModal && activeBaby && (
        <AddMedicineModal
          babyId={activeBaby.id}
          onSaved={() => { setShowAddMedicineModal(false); loadMedData(); }}
          onClose={() => setShowAddMedicineModal(false)}
        />
      )}

      <MealLogSheet
        isOpen={showMealSheet}
        plannedMeal={mealSheetSuggestion}
        onClose={() => { setShowMealSheet(false); setMealSheetSuggestion(undefined); }}
        onSaved={() => loadSolidsData()}
      />

      <WaterLogSheet
        isOpen={showWaterSheet}
        onClose={() => setShowWaterSheet(false)}
        onSaved={() => loadSolidsData()}
      />

    </div>
  );
};

export default Index;
